import {
  Avatar,
  ChakraBox,
  ChakraFlex,
  ChakraText,
  EquipUIFireflyV2Theme,
  FAIconName,
  FireflyButton,
  FireflyKebabMenu,
  FireflyKebabMenuItem,
  Icon,
  Tag,
  TagColor,
  Text,
  Tooltip,
} from '@equip.health/ui';
import { DateTime } from 'luxon';

import { NoteEvents } from '~/analytics';
import { capitalizeFirstLetter } from '~/lib/common';
import {
  NoteStatus,
  PatientNoteListItemType,
} from '~/lib/constants/AddPatientNoteModal.constants';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import { useGlobalContext } from '~/lib/contexts/GlobalContext';
import {
  getFormattedNoteActivityDateToDisplayString,
  getPreferredFullName,
} from '~/lib/util/patient.util';
import { useEditNoteDrawerContext } from '~/lib/contexts/EditNoteDrawerContext';
import {
  PortalComponent,
  useComponentPortalContext,
} from '~/lib/contexts/ComponentPortalContext';
import ProviderAvatarTooltipWrapper from '~/components/common/ProviderAvatarBlockTooltipWrapper';
import { AnalyticsSourceValues } from '~/lib/constants/AnalyticsSource.constants';

interface PatientNoteItemHeaderProps {
  note: PatientNoteListItemType;
  setOpenVoidNoteModal: (isOpen: boolean) => void;
  setOpenDeleteDraftModal: (isOpen: boolean) => void;
  groupClassEventDetails?: GroupClassEventDetails;
}

const { critical, neutral } = EquipUIFireflyV2Theme.colors;

const textFieldStyle = {
  alignSelf: 'center',
  border: '1px solid gray',
  borderColor: 'neutral.line',
  borderRadius: '6px',
  color: 'neutral.primary',
  fontSize: '13px',
  fontWeight: '500',
  padding: '4px 8px 4px 8px',
  whiteSpace: 'nowrap',
  width: 'auto',
};

const getNoteStatusText = (noteStatus: string): string => {
  switch (noteStatus) {
    case NoteStatus.DRAFT:
      return 'Draft';
    case NoteStatus.SIGNED:
      return 'Signed';
    case NoteStatus.VOID:
      return 'Void';
    default:
      return '';
  }
};

const getNoteStatusBackgroundColor = (noteStatus: string): TagColor => {
  switch (noteStatus) {
    case NoteStatus.DRAFT:
      return TagColor.marigold;
    case NoteStatus.SIGNED:
      return TagColor.green;
    case NoteStatus.VOID:
      return TagColor.red;
    default:
      return TagColor.lightGray;
  }
};

// Note: This is used in PatientItem which is used not only in the patient profile *but also* the view group class note accordion.
// Thanks to that, the latter flow must also be accounted for
const PatientNoteItemHeader = ({
  note,
  groupClassEventDetails,
  setOpenDeleteDraftModal,
  setOpenVoidNoteModal,
}: PatientNoteItemHeaderProps) => {
  const { trackEvent } = useAnalytics();
  const { currentUser, currentPatientId } = useGlobalContext();
  const { openNoteDrawer } = useEditNoteDrawerContext();
  const { setComponentVisibility } = useComponentPortalContext();

  const disabledTooltipForGroupNotesWhenInPatientProfile =
    "Editing isn't supported on the patient chart for group class notes. For hosts to edit a group class note, please find the event in your personal schedule.";

  const {
    activityDateTime,
    activityWiths,
    appointmentExternalId,
    authoredByProviderExternalId,
    createdByUser,
    groupClassEventExternalId,
    groupSectionEventExternalId,
    isConfidential,
    noteCommunicationMethod,
    noteExternalId,
    noteStatus,
    noteType,
    title,
  } = note;

  const TOP_LEVEL_ID = `patient-note-item-header__${noteExternalId}`;
  const OMIT_CHOSEN_NAME = '';

  const { firstName: providerFirstName, lastName: providerLastName } =
    createdByUser || {};

  const isNoteAuthor = authoredByProviderExternalId === currentUser.userId;
  const isCurrentUserHost = (groupClassEventDetails?.hosts ?? []).some(
    ({ externalId }: ScheduleUserBase) => externalId === currentUser.userId,
  );

  const disableEditNoteButton = groupClassEventExternalId
    ? !!currentPatientId || !isCurrentUserHost
    : !isNoteAuthor;

  const hideEditNoteButtonTooltip = !(
    !!groupClassEventExternalId &&
    currentPatientId &&
    disableEditNoteButton
  );

  const avatarName: string = getPreferredFullName(
    OMIT_CHOSEN_NAME,
    providerFirstName,
    providerLastName,
  );

  const kebabMenuOptions: FireflyKebabMenuItem[] = [];
  if (
    isNoteAuthor &&
    !groupClassEventExternalId &&
    !groupSectionEventExternalId
  ) {
    if (noteStatus === NoteStatus.DRAFT) {
      kebabMenuOptions.push({
        leftIcon: FAIconName.trashRegular,
        leftIconColor: 'critical.base',
        label: 'Delete',
        onClick: () => {
          trackEvent(NoteEvents.deleteDraftNoteButtonClicked, {
            Source: AnalyticsSourceValues.NOTES_TAB,
          });
          setOpenDeleteDraftModal(true);
        },
      });
    } else if (noteStatus === NoteStatus.SIGNED) {
      kebabMenuOptions.push({
        leftIcon: FAIconName.banRegular,
        leftIconColor: 'critical.base',
        label: 'Void',
        onClick: () => {
          trackEvent(NoteEvents.voidNotesTabButtonClicked, {
            Source: AnalyticsSourceValues.NOTES_TAB,
          });
          setOpenVoidNoteModal(true);
        },
      });
    }
  }

  const activityText = activityDateTime && (
    <Text
      color={neutral.secondary}
      id={`${TOP_LEVEL_ID}__activity-date-text`}
      style={{
        fontSize: '13px',
        letterSpacing: '-0.39px',
      }}
    >
      {`${avatarName} on ${getFormattedNoteActivityDateToDisplayString(
        DateTime.fromISO(activityDateTime).toJSDate(),
      )}`}
    </Text>
  );

  const avatarRow = (
    <ProviderAvatarTooltipWrapper
      actionTime={activityDateTime}
      isRoleAtAction
      placement="top"
      providerExternalId={createdByUser?.externalId}
      shouldShowDisplayName={false}
    >
      <ChakraFlex alignItems="center" flexDirection="row" gap="6px">
        <Avatar
          id={`${TOP_LEVEL_ID}__avatar-image`}
          name={avatarName}
          size="2xs"
          src={createdByUser?.avatar?.thumbnailUrl}
          textSize="xs"
          userType="provider"
        />
        {activityText}
      </ChakraFlex>
    </ProviderAvatarTooltipWrapper>
  );

  return (
    <>
      <ChakraFlex gap="24px" justifyContent="space-between" marginBottom="12px">
        <ChakraFlex gap="24px">
          <Text variant="h5">{title ?? '-'}</Text>
          {isConfidential && (
            <ChakraFlex alignItems="center">
              <Tooltip
                id={`${TOP_LEVEL_ID}__flag-tooltip`}
                placement="top-start"
                tooltipText="Flagged as confidential or sensitive"
              >
                <Icon
                  aria-label={FAIconName.flagSolid}
                  color={critical.base}
                  name={FAIconName.flagSolid}
                  size="sm"
                />
              </Tooltip>
            </ChakraFlex>
          )}
          {appointmentExternalId && (
            <ChakraFlex alignItems="center">
              <Icon
                color={neutral.secondary}
                name={FAIconName.linkRegular}
                size="sm"
              />
            </ChakraFlex>
          )}
          {avatarRow}
        </ChakraFlex>
        <ChakraFlex alignItems="center" gap="12px">
          {noteStatus === NoteStatus.DRAFT && (
            <ChakraBox marginRight="12px" paddingBottom="4px">
              <Tooltip
                id={`${TOP_LEVEL_ID}__disable-edit-note-tooltip`}
                isDisabled={hideEditNoteButtonTooltip}
                placement="left-start"
                tooltipText={disabledTooltipForGroupNotesWhenInPatientProfile}
              >
                <FireflyButton
                  id={`${TOP_LEVEL_ID}__edit-note`}
                  isDisabled={disableEditNoteButton}
                  onClick={() => {
                    trackEvent(NoteEvents.editNotesTabButtonClicked, {
                      Source: AnalyticsSourceValues.NOTES_TAB,
                    });
                    if (groupClassEventExternalId) {
                      setComponentVisibility({
                        component: PortalComponent.NOTE_ACCORDION,
                        data: {
                          existingNoteId: noteExternalId,
                          isAddNote: true,
                          noteData: [note],
                        },
                        isExpanded: true,
                        isVisible: true,
                      });
                    } else {
                      openNoteDrawer(currentPatientId, noteExternalId);
                    }
                  }}
                  rightIcon={FAIconName.penRegular}
                  size="small"
                  variant="tertiary"
                >
                  Edit note
                </FireflyButton>
              </Tooltip>
            </ChakraBox>
          )}
          {noteStatus && (
            <Tag
              color={getNoteStatusBackgroundColor(noteStatus)}
              data-testid={`${TOP_LEVEL_ID}__status-text`}
              id={`${TOP_LEVEL_ID}__status-text`}
              size="sm"
              value={getNoteStatusText(noteStatus)}
            />
          )}
          {kebabMenuOptions.length > 0 && (
            <ChakraFlex height="20px">
              <FireflyKebabMenu
                aria-label="Note header menu"
                options={kebabMenuOptions}
              />
            </ChakraFlex>
          )}
        </ChakraFlex>
      </ChakraFlex>

      <ChakraFlex flexDirection="row" gap="10px">
        {/**TODO: come up with a Text variant for this styling here */}
        <ChakraText {...textFieldStyle}>{noteType ? noteType : '-'}</ChakraText>
        <Text alignSelf="center">via</Text>
        <ChakraText {...textFieldStyle}>
          {noteCommunicationMethod
            ? noteCommunicationMethod?.charAt(0).toUpperCase() +
              noteCommunicationMethod?.slice(1)
            : '-'}
        </ChakraText>
        <Text alignSelf="center">with</Text>
        <ChakraText {...textFieldStyle}>
          {activityWiths?.length > 0
            ? activityWiths
                ?.map((activityWith) =>
                  capitalizeFirstLetter(activityWith.activityWithName),
                )
                ?.join(', ')
            : '-'}
        </ChakraText>
      </ChakraFlex>
    </>
  );
};

export default PatientNoteItemHeader;
